import React, { useEffect, useMemo, useState } from "react";
import { createIntl, createIntlCache, IntlProvider, useIntl } from "react-intl";
import LocaleGroup from "model/locale/locale_group";
import { FireQuery } from "helpers/firebase/firestore/firestore_collection";

const uniqueList = (items) => Array.from(new Set(items.filter(Boolean)));

const buildLocaleCandidates = (locale) => {
  if (!locale) {
    return [];
  }
  const lower = String(locale).toLowerCase();
  const underscore = lower.replace("-", "_");
  const base = lower.split("-")[0];
  return uniqueList([locale, lower, underscore, base, "en", "en-us", "en_us"]);
};

const selectLocaleValue = (entry, locale) => {
  if (!entry || typeof entry !== "object") {
    return undefined;
  }
  const candidates = buildLocaleCandidates(locale);
  for (const key of candidates) {
    if (entry[key] !== undefined) {
      return entry[key];
    }
  }
  return undefined;
};

const buildGroupMessages = (groupKey, locales, locale) => {
  const messages = {};
  if (!groupKey || !locales) {
    return messages;
  }

  const traverse = (prefix, obj) => {
    if (!obj || typeof obj !== "object") {
      return;
    }
    const value = selectLocaleValue(obj, locale);
    if (value !== undefined && typeof value !== "object") {
      messages[prefix] = value;
      return;
    }
    Object.keys(obj).forEach((key) => {
      traverse(`${prefix}.${key}`, obj[key]);
    });
  };

  traverse(groupKey, locales);
  return messages;
};

// New function to fetch local locale messages
const fetchLocalLocaleGroupMessages = async (groupKey, locale) => {
  if (!groupKey || !locale) {
    return {};
  }

  const normalizedLocale = locale.substring(0, 2).toLowerCase();
  try {
    // Expected folder structure for local files: src/locales/{locale}/{groupKey}.json
    // For example: src/locales/en/common.json
    // This dynamic import relies on the bundler (e.g., Webpack) to resolve the path.
    // If you encounter issues, you might need to configure your bundler
    // to handle dynamic imports with variable paths, or use a pre-generated index file.
    const messagesModule = await import(`../locales/${normalizedLocale}/${groupKey}.json`);
    return messagesModule.default || messagesModule; // Handle default export for JSON files
  } catch (error) {
    // It's expected for some local files not to exist, so we only log a warning if needed.
    // console.warn(`Local locale file not found or invalid for ${groupKey}/${normalizedLocale}:`, error);
    return {}; // Return empty object if file not found or parsing error
  }
};


export const fetchLocaleGroupMessages = async (groupKey, locale, localOnly = false, onlineOnly = false) => {
  if (locale) {
    locale = locale.substring(0, 2).toLowerCase();
  }

  if (!groupKey) {
    return {};
  }

  let localMessages = {};
  let onlineMessages = {};

  // Fetch local messages if not onlineOnly
  if (!onlineOnly) {
    localMessages = await fetchLocalLocaleGroupMessages(groupKey, locale);
  }

  // Fetch online messages if not localOnly
  if (!localOnly) {
    try {
      // Wrap the existing callback-based getDocs into a Promise
      const results = await new Promise((resolve, reject) => {
        LocaleGroup.collection
          .getDocs([new FireQuery("group_key", "==", groupKey)], resolve)
          .catch(reject);
      });

      const group = Array.isArray(results) && results.length > 0 ? results[0] : null;
      if (group) {
        onlineMessages = buildGroupMessages(groupKey, group.locales || {}, locale);
      }
    } catch (error) {
      console.error(`Failed to fetch online locale group for ${groupKey}:`, error);
      // Continue with local messages even if online fails
    }
  }

  // Merge logic: local first, then online.
  // If both localOnly and onlineOnly are true, it acts as if both are false (merge).
  if (localOnly && onlineOnly) {
    return { ...localMessages, ...onlineMessages };
  } else if (localOnly) {
    return localMessages;
  } else if (onlineOnly) {
    return onlineMessages;
  } else { // Default case: !localOnly && !onlineOnly, merge local then online
    return { ...localMessages, ...onlineMessages };
  }
};

export const fetchLocaleGroupMessagesMultiple = (groupKeys, locale, localOnly = false, onlineOnly = false) => {
  if (!groupKeys || groupKeys.length === 0) {
    return Promise.resolve({});
  }

  const promises = groupKeys.map((key) => fetchLocaleGroupMessages(key, locale, localOnly, onlineOnly));

  return Promise.all(promises).then((results) => {
    return results.reduce((accumulator, messages) => {
      return { ...accumulator, ...messages };
    }, {});
  });
};

const intlCache = createIntlCache();

export const useIntlWithLocalGroups = (groupKeys, localOnly = false, onlineOnly = false) => {
  const intl = useIntl();
  const normalizedGroupKeys = useMemo(() => {
    const keys = !groupKeys ? [] : Array.isArray(groupKeys) ? groupKeys : [groupKeys];
    return keys;
// Stringify the array so the memo only breaks if the actual text within the array changes
  }, [JSON.stringify(groupKeys)]);

  const [groupMessages, setGroupMessages] = useState({});
  const [loading, setLoading] = useState(normalizedGroupKeys.length > 0);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (normalizedGroupKeys.length === 0) {
      setGroupMessages({});
      setLoading(false);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    fetchLocaleGroupMessagesMultiple(normalizedGroupKeys, intl.locale, localOnly, onlineOnly)
      .then((messages) => {
        if (isMounted) {
          setGroupMessages(messages || {});
          setLoading(false);
        }
      })
      .catch((fetchError) => {
        console.error("Failed to load locale group", fetchError);
        if (isMounted) {
          setGroupMessages({});
          setLoading(false);
          setError(fetchError);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [normalizedGroupKeys, intl.locale, localOnly, onlineOnly]); // Add localOnly and onlineOnly to dependencies

  const mergedMessages = useMemo(() => {
    return { ...(intl.messages || {}), ...(groupMessages || {}) };
  }, [intl.messages, groupMessages]);

  const mergedIntl = useMemo(
    () => createIntl({ locale: intl.locale, messages: mergedMessages }, intlCache),
    [intl.locale, mergedMessages]
  );

  return { intl: mergedIntl, groupMessages, mergedMessages, loading, error };
};

export const LocaleGroupProvider = ({ groupKeys, localOnly = false, onlineOnly = false, children }) => {
  const { intl, mergedMessages } = useIntlWithLocalGroups(groupKeys, localOnly, onlineOnly);
  const normalizedGroupKeys = useMemo(() => {
    if (!groupKeys) {
      return [];
    }
    return Array.isArray(groupKeys) ? groupKeys : [groupKeys];
  }, [groupKeys]);

  if (normalizedGroupKeys.length === 0) {
    return children;
  }

  return (
    <IntlProvider locale={intl.locale} messages={mergedMessages}>
      {children}
    </IntlProvider>
  );
};

export const withLocaleGroup = (groupKeys, localOnly = false, onlineOnly = false) => (WrappedComponent) => {
  const WithLocaleGroup = (props) => (
    <LocaleGroupProvider groupKeys={groupKeys} localOnly={localOnly} onlineOnly={onlineOnly}>
      <WrappedComponent {...props} />
    </LocaleGroupProvider>
  );
  WithLocaleGroup.displayName = `WithLocaleGroup(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
  return WithLocaleGroup;
};
